
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Eye, CheckCircle, Clock, MapPin, User, Plus, History, Save } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface AlertDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  alert: any;
  type: 'emergency' | 'observation';
  onUpdate?: () => void;
}

interface HistoryEntry {
  id: string;
  action: string;
  details: string;
  timestamp: string;
  user_name: string;
}

export const AlertDetailDialog: React.FC<AlertDetailDialogProps> = ({
  isOpen,
  onClose,
  alert,
  type,
  onUpdate
}) => {
  const { toast } = useToast();
  const [newNote, setNewNote] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [notes, setNotes] = useState<any[]>([]);

  useEffect(() => {
    if (alert && isOpen) {
      setSelectedStatus(alert.status || '');
      loadNotesAndHistory();
    }
  }, [alert, isOpen]);

  const loadNotesAndHistory = async () => {
    if (!alert) return;

    try {
      // Load existing notes from the alert data
      const existingNotes = alert.notes || [];
      setNotes(existingNotes);

      // Create mock history for demonstration
      const mockHistory: HistoryEntry[] = [
        {
          id: '1',
          action: 'Created',
          details: `${type === 'emergency' ? 'Emergency report' : 'Patrol observation'} created`,
          timestamp: alert.created_at || alert.timestamp,
          user_name: alert.guard_name || 'System'
        }
      ];

      if (alert.status === 'resolved') {
        mockHistory.push({
          id: '2',
          action: 'Status Changed',
          details: 'Status changed to resolved',
          timestamp: alert.resolved_at || new Date().toISOString(),
          user_name: alert.resolved_by || 'Admin'
        });
      }

      setHistory(mockHistory);
    } catch (error) {
      console.error('Error loading notes and history:', error);
    }
  };

  const handleStatusChange = async () => {
    if (!alert || !selectedStatus || selectedStatus === alert.status) return;

    try {
      const tableName = type === 'emergency' ? 'emergency_reports' : 'patrol_observations';
      const updateData: any = { status: selectedStatus };

      if (selectedStatus === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = 'Admin'; // In a real app, this would be the current user
      }

      const { error } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', alert.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Status updated to ${selectedStatus}`,
      });

      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const addNote = async () => {
    if (!alert || !newNote.trim()) return;

    try {
      const noteEntry = {
        id: Date.now().toString(),
        content: newNote,
        created_at: new Date().toISOString(),
        author: 'Admin' // In a real app, this would be the current user
      };

      const updatedNotes = [...notes, noteEntry];
      
      const tableName = type === 'emergency' ? 'emergency_reports' : 'patrol_observations';
      const { error } = await supabase
        .from(tableName)
        .update({ notes: updatedNotes })
        .eq('id', alert.id);

      if (error) throw error;

      setNotes(updatedNotes);
      setNewNote('');
      setIsAddingNote(false);

      toast({
        title: 'Success',
        description: 'Note added successfully',
      });

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note',
        variant: 'destructive',
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-blue-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-500';
      case 'investigating':
        return 'bg-blue-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'investigating':
        return <Eye className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusOptions = () => {
    if (type === 'emergency') {
      return ['pending', 'investigating', 'resolved'];
    } else {
      return ['pending', 'resolved'];
    }
  };

  if (!alert) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {type === 'emergency' ? (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            ) : (
              <Eye className="h-5 w-5 text-blue-500" />
            )}
            {alert.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Badges and Status Management */}
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {alert.severity && (
                <Badge className={`${getSeverityColor(alert.severity)} text-white`}>
                  {alert.severity}
                </Badge>
              )}
              {alert.status && (
                <Badge className={`${getStatusColor(alert.status)} text-white`}>
                  {alert.status}
                </Badge>
              )}
            </div>

            {/* Status Change Section */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Change Status:</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {getStatusOptions().map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStatus !== alert.status && (
                <Button onClick={handleStatusChange} size="sm">
                  <Save className="w-4 h-4 mr-1" />
                  Update
                </Button>
              )}
            </div>
          </div>

          {/* Description */}
          {alert.description && (
            <div>
              <h3 className="text-sm font-medium mb-2">Description</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {alert.description}
              </p>
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {type === 'emergency' ? 'Reported' : 'Observed'}
              </h4>
              <p className="text-sm">
                {format(new Date(type === 'emergency' ? alert.created_at : alert.timestamp), 'PPpp')}
              </p>
            </div>

            {alert.guard_name && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Reported By
                </h4>
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4 text-gray-400" />
                  <p className="text-sm">{alert.guard_name}</p>
                </div>
              </div>
            )}

            {alert.location && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Location
                </h4>
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <p className="text-sm">{alert.location}</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                ID
              </h4>
              <p className="text-sm font-mono">{alert.id}</p>
            </div>
          </div>

          {/* Image Section */}
          {alert.image_url && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium mb-2">Image</h3>
              <div className="relative">
                <img 
                  src={alert.image_url} 
                  alt={alert.title}
                  className="rounded-lg w-full max-w-md max-h-96 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(alert.image_url, '_blank')}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                  <Eye className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Notes Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Notes</h3>
              <Button
                onClick={() => setIsAddingNote(!isAddingNote)}
                size="sm"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Note
              </Button>
            </div>

            {isAddingNote && (
              <div className="space-y-3 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
                <Textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="min-h-[80px]"
                />
                <div className="flex gap-2">
                  <Button onClick={addNote} size="sm">
                    Save Note
                  </Button>
                  <Button 
                    onClick={() => {
                      setIsAddingNote(false);
                      setNewNote('');
                    }} 
                    size="sm" 
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {notes.length > 0 && (
              <div className="space-y-3">
                {notes.map((note, index) => (
                  <div key={note.id || index} className="p-3 border rounded-lg bg-white dark:bg-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{note.author}</span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(note.created_at), 'PPp')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* History Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">History</h3>
              <Button
                onClick={() => setShowHistory(!showHistory)}
                size="sm"
                variant="outline"
              >
                <History className="w-4 h-4 mr-1" />
                {showHistory ? 'Hide' : 'Show'} History
              </Button>
            </div>

            {showHistory && (
              <div className="space-y-3">
                {history.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-3 p-3 border rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{entry.action}</span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(entry.timestamp), 'PPp')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{entry.details}</p>
                      <p className="text-xs text-gray-400 mt-1">by {entry.user_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
